import React from 'react';
import { filterData } from '../dataVisualizationsHelpers.js';
import { NO_DATA_FOUND, QUARTILE_DICTIONARY } from '../../../static/constants.js';
import {
  PROGRAM_ACCOUNT_TYPES,
  SUB_FILTER_TO_PROGRAM_LABELS,
  PROGRAM_ACCOUNT_TYPES_WITHOUT_REVENUE
} from '../dataVisualizationsHelpers.js';
import { FUND, PROGRAM } from '../../../static/keyWords.js';
import { messageManager } from '../../System/messageManager/messageManager.js';
import { NAVIGATION_PATHS } from '../../../static/navigationPaths.js';
import GeneralEmptyState from '../../reusable/EmptyState/GeneralEmptyState/GeneralEmptyState.js';
import { buildPath } from '../../../services/navigateService.js';

const VIEW_MODES = {
  BUDGETS: 'Budgets',
  FUND: 'Fund',
};

const BUDGET_UTILS = {
  barThickness: 10,
  color: {
    first: '#4CAF50',
    second: '#9CCC65DE',
  },
};

const QUARTILE_UTILS = {
  barThickness: 15,
  color: {
    first: '#448AFF',
    second: '#00897B',
    third: '#FF9100',
    fourth: '#FFB300',
  },
  barGroupThickness: 45,
};

const INITIAL_CURRENT_BUDGET_AND_QUARTILE = {
  isOpen: false,
  currentBudget: {},
  selectedQuartile: '',
};

const getTotalCostOfFilteredPrograms = (programs, selectedFilters, budgetsCompareSelected) => {
  let filteredPrograms;
  let totalCost;
  let _filteredProgramsByFund = programs;
  let _selectedFilters = { ...selectedFilters };

  if (budgetsCompareSelected) {
    const filteredProgramsByFund = filterProgramsByFund([...programs], selectedFilters);
    _filteredProgramsByFund = filteredProgramsByFund;
    delete _selectedFilters.Fund;
  }
  // On every rerender, assign original items to filteredItems and apply filters on filteredItems.
  programs.forEach((program) => (program.filteredItems = program.items));
  //Remove filter for which we performed custom logic
  _selectedFilters = getSelectedFiltersWithoutFund(_selectedFilters);

  filteredPrograms = filterData(_filteredProgramsByFund, _selectedFilters);

  totalCost = filteredPrograms.reduce((totalCostSum, program) => {
    return totalCostSum + getTotalCostOfProgramLineItems(program);
  }, 0);
  return totalCost || 0;
};

const getTotalCostOfProgramLineItems = (program) => {
  return ['personnel', 'nonPersonnel', 'revenue'].reduce(
    (totalCostOfItems, accountType) =>
      totalCostOfItems +
      program.filteredItems[accountType].reduce((costOfItemsInAccountType, item) => {
        if (item) {
          return costOfItemsInAccountType + item.cost;
        }

        return costOfItemsInAccountType;
      }, 0),
    0
  );
};

const filterBudgetsById = (selectedFilters, budgets) => {
  let subFilterValuesArray = [];
  let filteredBudgets;

  for (let subFilter of Object.values(selectedFilters)) {
    if (subFilter['Budget']) {
      subFilterValuesArray.push(...subFilter['Budget']);
    }
  }

  if (subFilterValuesArray.length !== 0) {
    filteredBudgets = budgets.filter((budget) => subFilterValuesArray.includes(`${budget.budgetName}-${budget.budgetYear}`));
  } else filteredBudgets = budgets;

  return filteredBudgets;
};

const refineDatasets = (filteredBudgetsById, selectedFilters, budgetsCompareSelected) => {
  return {
    [QUARTILE_DICTIONARY[1]]: {
      isShown: true,
      dataset: {
        label: QUARTILE_DICTIONARY[1],
        backgroundColor: '#00897B',
        borderColor: '#00897B',
        data: filteredBudgetsById.map((budget) => getTotalCostOfFilteredPrograms(budget.mostAligned.programs, selectedFilters, budgetsCompareSelected)),
      },
    },
    [QUARTILE_DICTIONARY[2]]: {
      isShown: true,
      dataset: {
        label: QUARTILE_DICTIONARY[2],
        backgroundColor: '#448AFF',
        borderColor: '#448AFF',
        data: filteredBudgetsById.map((budget) => getTotalCostOfFilteredPrograms(budget.moreAligned.programs, selectedFilters, budgetsCompareSelected)),
      },
    },
    [QUARTILE_DICTIONARY[3]]: {
      isShown: true,
      dataset: {
        label: QUARTILE_DICTIONARY[3],
        backgroundColor: '#FF9100',
        borderColor: '#FF9100',
        data: filteredBudgetsById.map((budget) => getTotalCostOfFilteredPrograms(budget.lessAligned.programs, selectedFilters, budgetsCompareSelected)),
      },
    },
    [QUARTILE_DICTIONARY[4]]: {
      isShown: true,
      dataset: {
        label: QUARTILE_DICTIONARY[4],
        backgroundColor: '#FDD835',
        borderColor: '#FDD835',
        data: filteredBudgetsById.map((budget) => getTotalCostOfFilteredPrograms(budget.leastAligned.programs, selectedFilters, budgetsCompareSelected)),
      },
    },
  };
};

const filterFundsById = (selectedFilters, funds) => {
  let subFilterValuesArray = [];
  let filteredFunds;

  for (let subFilter of Object.values(selectedFilters)) {
    if (subFilter['Fund']) {
      subFilterValuesArray.push(...subFilter['Fund']);
    }
  }

  if (subFilterValuesArray.length !== 0) {
    filteredFunds = funds.filter((fund) => subFilterValuesArray.includes(fund.fundName));
  } else filteredFunds = funds;
  return filteredFunds;
};

const filterProgramsByFund = (programs, selectedFilters) => {
  let subFilterValuesArray = [];

  for (let subFilter of Object.values(selectedFilters)) {
    if (subFilter['Fund']) {
      subFilterValuesArray.push(...subFilter['Fund']);
    }
  }

  if (subFilterValuesArray.length !== 0) {
    programs = programs.filter(
      (program) => program.funds && subFilterValuesArray.some((subFilterValue) => program.funds.some((fund) => fund === subFilterValue))
    );
  }

  return programs;
};

const createBarChartData = (datasets, labels, state) => {
  let barChartData = {};
  if (datasets) {
    barChartData = {
      labels: labels,
      datasets: datasets,
      color: state.theme === 'light' ? ['#422481', '#765DAC', '#4845E7', '#5BD8F1'] : ['#2E97F3', '#4FB477', '#FFBF46', '#FB4D3D'],
    };
  }
  return barChartData;
};

const createBarChartOptions = (state) => {
  return {
    indexAxis: 'y',
    maintainAspectRatio: false,
    plugins: {
      legend: false,
      tooltip: {
        callbacks: {
          title: (context) => context[0].label.replaceAll(',', ' '),
        },
        backgroundColor: '#1A1A1A',
        font: {
          weight: 600,
          color: '#E0E3FB',
        },
        borderColor: 'black',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            weight: 400,
            family: 'Roboto',
            size: 12,
          },
          color: state.theme === 'light' ? 'black' : '#E0E3FB',
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          font: {
            weight: 400,
            family: 'Roboto',
            size: 12,
          },
          color: state.theme === 'light' ? 'black' : '#E0E3FB',
        },
      },
    },
  };
};

const setHeightOfBarChartContainer = (itemsCount, ref, fundsTab) => {
  let height;
  if (itemsCount <= 5) height = itemsCount * 150;
  else {
    if (fundsTab) height = itemsCount * 70;
    else height = itemsCount * 70;
  }

  let container = ref.current;
  container.style.height = `${height}px`;
};

const getActiveBudget = (allBudgets, user) => {
  let budgetWithPrograms;
  let programs;
  let activeBudget = user.activeBudget;
  if (allBudgets.length !== 0) {
    budgetWithPrograms = allBudgets.filter((budget) => budget.budgetId === activeBudget.id)[0];
    programs = [
      ...budgetWithPrograms.mostAligned.programs,
      ...budgetWithPrograms.moreAligned.programs,
      ...budgetWithPrograms.lessAligned.programs,
      ...budgetWithPrograms.leastAligned.programs,
    ];
  }
  return [activeBudget, programs];
};

const setQuartileVerbalCamelCase = (value) => {
  let [firstPart, secondPart] = value.split(' ');
  return firstPart.toLowerCase() + secondPart.charAt(0).toUpperCase() + secondPart.slice(1);
};

const getTopProgramsWithMostChanges = (programs, selectedCount) => {
  let slicedProgramsCount = [];
  if (!selectedCount) slicedProgramsCount = programs;
  else slicedProgramsCount = programs.slice(0, selectedCount);
  return slicedProgramsCount;
};

const getProgramRange = (nameConventions) => {
  return {
    ALL: `All ${nameConventions[PROGRAM].pluralFormAlias}`,
    TOP_10: `Top 10 ${nameConventions[PROGRAM].pluralFormAlias}`,
    TOP_50: `Top 50 ${nameConventions[PROGRAM].pluralFormAlias}`,
  };
};

const handleFilterBudgetDropDownOptions = (value, option) => {
  return option
    .map((filterOption) => {
      return {
        label:
          `${filterOption.budgetName} ${filterOption.budgetYear}`.length > 20
            ? `${filterOption.budgetName} ${filterOption.budgetYear}`.substring(0, 18) + '...'
            : `${filterOption.budgetName} ${filterOption.budgetYear}`,
        value: {
          label: `${filterOption.budgetName} ${filterOption.budgetYear}`,
          id: filterOption.budgetId,
        },
      };
    })
    .filter((filterOption) => filterOption.label.toLowerCase().includes(value.toLowerCase().trim()));
};

const filterQuartileOptions = (value, options) => {
  return options
    .map((option) => {
      return {
        label: option,
        value: option,
      };
    })
    .filter((option) => option.label.toLowerCase().includes(value.toLowerCase().trim()));
};

const customFilterItemsOfProgram = (program, subFilterLabel, selected) => {
  //Filters program items in place and returns T/F depending on whether there are any items left
  let itemsBudgetOne = program.currentBudgetProgramItems;
  let itemsBudgetTwo = program.selectedBudgetProgramItems;
  program.filteredItemsBudgetOne = {};
  program.filteredItemsBudgetTwo = {};
  let atLeastOneItem = false;
  PROGRAM_ACCOUNT_TYPES.forEach((accountType) => {
    let filteredItemsBudgetOne = filterItems(itemsBudgetOne[accountType], subFilterLabel, selected);
    let filteredItemsBudgetTwo = filterItems(itemsBudgetTwo[accountType], subFilterLabel, selected);
    program.filteredItemsBudgetOne[accountType] = filteredItemsBudgetOne;
    program.filteredItemsBudgetTwo[accountType] = filteredItemsBudgetTwo;
    if (filteredItemsBudgetOne.length + filteredItemsBudgetTwo.length > 0) atLeastOneItem = true;
  });
  return atLeastOneItem;
};

const filterItems = (items, subFilterLabel, selected) => {
  return items.filter((item) => selected.includes(item[SUB_FILTER_TO_PROGRAM_LABELS[subFilterLabel]]));
};

const getSumOfItemCosts = (program, budgetKey) => {
  return PROGRAM_ACCOUNT_TYPES_WITHOUT_REVENUE.reduce(
    (totalCost, accountType) => totalCost + program[budgetKey][accountType].reduce((totalAccountTypeCost, item) => totalAccountTypeCost + item.cost, 0),
    0
  );
};

const addFilteredItemsToProgramsObj = (programs) => {
  programs.forEach((program) => {
    program.filteredItemsBudgetOne = program.currentBudgetProgramItems;
    program.filteredItemsBudgetTwo = program.selectedBudgetProgramItems;
  });
};

const getSelectedFiltersWithoutFund = (selectedFilters) => {
  return Object.keys(selectedFilters)
    .filter((key) => key != FUND)
    .reduce((obj, key) => {
      obj[key] = selectedFilters[key];
      return obj;
    }, {});
};

const determineFiltersTabAndSubTab = (currentTab, selectedBudgetOrFundContext) => {
  return currentTab === 1 ? (selectedBudgetOrFundContext === VIEW_MODES.BUDGETS ? 1 : 5) : currentTab;
};

const getQuartiles = (filteredFundsById, selectedFilters) => {
  return {
    [QUARTILE_DICTIONARY[1]]: {
      isShown: true,
      dataset: {
        label: QUARTILE_DICTIONARY[1],
        backgroundColor: QUARTILE_UTILS.color.first,
        borderColor: QUARTILE_UTILS.color.first,
        data: filteredFundsById.map(fund => getTotalCostOfFilteredPrograms(fund.firstQuartilePrograms, selectedFilters)),
        barThickness: QUARTILE_UTILS.barThickness
      }
    },
    [QUARTILE_DICTIONARY[2]]: {
      isShown: true,
      dataset: {
        label: QUARTILE_DICTIONARY[2],
        backgroundColor: QUARTILE_UTILS.color.second,
        borderColor: QUARTILE_UTILS.color.second,
        data: filteredFundsById.map(fund => getTotalCostOfFilteredPrograms(fund.secondQuartilePrograms, selectedFilters)),
        barThickness: QUARTILE_UTILS.barThickness
      }
    },
    [QUARTILE_DICTIONARY[3]]: {
      isShown: true,
      dataset: {
        label: QUARTILE_DICTIONARY[3],
        backgroundColor: QUARTILE_UTILS.color.third,
        borderColor: QUARTILE_UTILS.color.third,
        data: filteredFundsById.map(fund => getTotalCostOfFilteredPrograms(fund.thirdQuartilePrograms, selectedFilters)),
        barThickness: QUARTILE_UTILS.barThickness
      }
    },
    [QUARTILE_DICTIONARY[4]]: {
      isShown: true,
      dataset: {
        label: QUARTILE_DICTIONARY[4],
        backgroundColor: QUARTILE_UTILS.color.fourth,
        borderColor: QUARTILE_UTILS.color.fourth,
        data: filteredFundsById.map(fund => getTotalCostOfFilteredPrograms(fund.fourthQuartilePrograms, selectedFilters)),
        barThickness: QUARTILE_UTILS.barThickness
      },
    }
  };
};

const showBudgetsAndFundChart = (allDatasets, allBudgetsToCompare) => {
  return Object.entries(allDatasets).length &&
    allBudgetsToCompare.length &&
    Object.values(allDatasets).filter((set) => set.isShown && set.dataset.data.some(value => value > 0)).length;
};

const noData = (programs, navigate, user, nameConventions) => {
  const messages = messageManager(nameConventions).DATA_VISUALIZATIONS;

  const message = programs.length
    ? messages.NO_ALLOCATIONS_FOUND
    : messages.NO_PROGRAMS_FOUND;
  const buttonTitle = programs.length
    ? messages.NO_ALLOCATIONS_FOUND_REDIRECT
    : messages.REDIRECTION_BUTTON;
  const redirect = programs.length
    ? buildPath(user, NAVIGATION_PATHS.COST_ALLOCATION)
    : buildPath(user, NAVIGATION_PATHS.PROGRAM_INVENTORY);

  return (
    <GeneralEmptyState
      alt={NO_DATA_FOUND.alt}
      src={NO_DATA_FOUND.source}
      title={message}
      buttonTitle={buttonTitle}
      handleClick={() => navigate(redirect)}
    />
  );
};

export {
  VIEW_MODES,
  INITIAL_CURRENT_BUDGET_AND_QUARTILE,
  BUDGET_UTILS,
  QUARTILE_UTILS,
  getTotalCostOfFilteredPrograms,
  filterBudgetsById,
  filterFundsById,
  filterProgramsByFund,
  createBarChartData,
  createBarChartOptions,
  setHeightOfBarChartContainer,
  getActiveBudget,
  refineDatasets,
  setQuartileVerbalCamelCase,
  getTopProgramsWithMostChanges,
  getProgramRange,
  handleFilterBudgetDropDownOptions,
  filterQuartileOptions,
  customFilterItemsOfProgram,
  getSumOfItemCosts,
  addFilteredItemsToProgramsObj,
  getSelectedFiltersWithoutFund,
  determineFiltersTabAndSubTab,
  getQuartiles,
  showBudgetsAndFundChart,
  noData,
};
